import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { EMPTY_ARRAY_LENGTH, USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { GetGroupTranslationsDto } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';

@UntilDestroy()
@Component({
    selector: 'aumico-templates-edit-groups-translations',
    templateUrl: './edit-groups-translations.component.html',
    styleUrls: ['./edit-groups-translations.component.scss'],
})
export class EditGroupsTranslationsComponent {
    public formGroup: UntypedFormGroup | undefined;
    public translationsFormArray: UntypedFormArray | undefined;
    public displayedColumns: string[] = ['name', 'currentLocale', 'selectedLocale'];
    public isCompleted: boolean;

    private groupsList: GetGroupTranslationsDto[] = [];
    @Input() set tableData(value: GetGroupTranslationsDto[]) {
        if (value != null) {
            this.groupsList = value;
            this.buildForm();
        }
    }

    public selectedLocaleValue: string | undefined;
    @Input() set selectedLocale(value: string | undefined) {
        if (value != null) {
            this.selectedLocaleValue = value.toLowerCase();
            this.buildForm();
        }
    }

    @Input() currentLocale = 'de-ch';
    @Input() panelOpenState = false;

    @Output() translationValuesChanged: EventEmitter<Omit<GetGroupTranslationsDto, 'name'>[]> = new EventEmitter<
        Omit<GetGroupTranslationsDto, 'name'>[]
    >();

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
    ) {
        this.addSvgIcon('aumico-pattern-icon-check-circle');
        this.addSvgIcon('aumico-pattern-icon-wrong-circle');
        this.buildForm();
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    private buildForm(): void {
        this.formGroup = this.formBuilder.group({
            translationsFormArray: this.formBuilder.array([]),
        });
        this.translationsFormArray = this.getAllAsFormArray();

        this.monitorFormChanges();

        this.formGroup.setControl('translationsFormArray', this.translationsFormArray);
        this.formGroup.updateValueAndValidity();
    }

    private getAllAsFormArray(): UntypedFormArray {
        const tableData = this.getTableRows();
        const formGroups: AbstractControl[] = tableData.map((entry) => this.getTableEntryFormGroup(entry));
        return new UntypedFormArray(formGroups);
    }

    private getTableRows(): IDynamicKeyValue[] {
        const tableData: IDynamicKeyValue[] = [];
        this.groupsList.forEach((group) => {
            tableData.push({
                name: group.name + ' - ' + 'title',
                currentLocale: group.title[this.currentLocale],
                selectedLocale: group.title[this.selectedLocaleValue],
                _id: group._id,
            });

            tableData.push({
                name: group.name + ' - ' + 'total name',
                currentLocale: group.totalName[this.currentLocale],
                selectedLocale: group.totalName[this.selectedLocaleValue],
                _id: group._id,
            });
        });
        return tableData;
    }

    private getTableEntryFormGroup(tableEntry: IDynamicKeyValue): UntypedFormGroup {
        const dataSourceFormGroup: { [key: string]: AbstractControl } = {};
        dataSourceFormGroup['_id'] = new UntypedFormControl(tableEntry['_id']);
        dataSourceFormGroup['name'] = new UntypedFormControl(tableEntry['name']);
        dataSourceFormGroup[this.currentLocale] = new UntypedFormControl(tableEntry['currentLocale']);
        dataSourceFormGroup[this.selectedLocaleValue] = new UntypedFormControl(tableEntry['selectedLocale']);

        return new UntypedFormGroup(dataSourceFormGroup);
    }

    private monitorFormChanges(): void {
        if (this.formGroup == null) {
            throw new Error('Form group is undefined.');
        }
        this.formGroup.valueChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), untilDestroyed(this))
            .subscribe((formValue) => {
                const formValueArray = formValue.translationsFormArray;

                const translationResults: Omit<GetGroupTranslationsDto, 'name'>[] = [];

                // since one row is for the group title and one for the group name
                // we loop on them and create an object for each group with odd index
                // and get the total name from the even rows and add them to that object as well
                for (let i = 0; i < formValueArray.length; i += 2) {
                    const currentObj = formValueArray[i];
                    const nextObj = formValueArray[i + 1];

                    if (nextObj && currentObj._id === nextObj._id) {
                        translationResults.push({
                            _id: currentObj._id,
                            title: {
                                [this.currentLocale]: currentObj[this.currentLocale],
                                [this.selectedLocaleValue]: currentObj[this.selectedLocaleValue],
                            },
                            totalName: {
                                [this.currentLocale]: nextObj[this.currentLocale],
                                [this.selectedLocaleValue]: nextObj[this.selectedLocaleValue],
                            },
                        });
                    } else {
                        translationResults.push(currentObj);
                        if (nextObj) {
                            translationResults.push(nextObj);
                        }
                    }
                }

                this.translationValuesChanged.emit(translationResults);
                this.checkIfTranslationsAreCompleted(translationResults);
            });
    }

    private checkIfTranslationsAreCompleted(translationResults: Omit<GetGroupTranslationsDto, 'name'>[]): void {
        this.isCompleted = true;
        translationResults.forEach((translation) => {
            if (
                translation.title != null &&
                translation.title[this.selectedLocaleValue] != null &&
                translation.title[this.selectedLocaleValue].length > EMPTY_ARRAY_LENGTH &&
                translation.title[this.currentLocale] != null &&
                translation.title[this.currentLocale].length > EMPTY_ARRAY_LENGTH &&
                translation.totalName != null &&
                translation.totalName[this.selectedLocaleValue] != null &&
                translation.totalName[this.selectedLocaleValue].length > EMPTY_ARRAY_LENGTH &&
                translation.totalName[this.currentLocale] != null &&
                translation.totalName[this.currentLocale].length > EMPTY_ARRAY_LENGTH
            ) {
                this.isCompleted = this.isCompleted && true;
            } else {
                this.isCompleted = false;
            }
        });
    }
}
